import { Button, Form, Input, Spin } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { LeftOutlined, LoadingOutlined } from "@ant-design/icons";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactComponent as QuincyIcon } from "shared/assets/images/icon-quincy-pitching.svg";
import { ReactComponent as PencilIcon } from "shared/assets/images/pencil-icon.svg";
import "./ReleaseScheduleQuincy.scss";

interface ReleaseScheduleQuincyProps {
  generateContent: (params?: any) => Promise<string>;
  fieldName: string;
  fieldNameSuggested?: string;
  isVisible?: boolean;
  required?: boolean;
}

enum CurrentAction {
  None,
  Editing,
  Generating,
}

const ReleaseScheduleQuincy: React.FC<ReleaseScheduleQuincyProps> = ({
  generateContent,
  fieldName,
  fieldNameSuggested,
  isVisible = false,
  required = false,
}) => {
  const { t } = useTranslation();
  const hasAutoGeneratedWhenVisible = useRef(false);
  const [currentAction, setCurrentAction] = useState<CurrentAction>(CurrentAction.None);
  const [prompt, setPrompt] = useState("");
  const [previousSchedule, setPreviousSchedule] = useState("");

  const form = Form.useFormInstance();

  const onCancelEdit = () => {
    setCurrentAction(CurrentAction.None);
    form.setFieldValue(fieldName, previousSchedule);
  };

  const onEdit = () => {
    setCurrentAction(CurrentAction.Editing);
    setPreviousSchedule(form.getFieldValue(fieldName));
  };

  const onKeyDownPrompt = async (ev: React.KeyboardEvent) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      await regenerateQuincy();
    }
  };

  const onConfirmEdit = () => {
    setCurrentAction(CurrentAction.None);
  };

  const regenerateQuincy = async () => {
    if (currentAction === CurrentAction.Generating) return;

    setCurrentAction(CurrentAction.Generating);
    try {
      const response = await generateContent({
        prompt,
        currentText: form.getFieldValue(fieldName),
      });
      form.setFieldValue(fieldName, response);
      if (fieldNameSuggested) {
        form.setFieldValue(fieldNameSuggested, response);
      }
      setPrompt("");
    } finally {
      setCurrentAction(CurrentAction.None);
    }
  };

  useEffect(() => {
    if (!isVisible || hasAutoGeneratedWhenVisible.current) return;
    hasAutoGeneratedWhenVisible.current = true;
    const run = async () => {
      setCurrentAction(CurrentAction.Generating);
      try {
        const response = await generateContent();
        form.setFieldValue(fieldName, response);
        if (fieldNameSuggested) {
          form.setFieldValue(fieldNameSuggested, response);
        }
      } finally {
        setCurrentAction(CurrentAction.None);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  return (
    <div className="wrapper-quincy">
      {fieldNameSuggested && (
        <Form.Item name={fieldNameSuggested} hidden>
          <Input />
        </Form.Item>
      )}
      <div className="header">
        {currentAction === CurrentAction.Editing ? (
          <button className="btn-cancel" onClick={onCancelEdit} type="button">
            <LeftOutlined />
          </button>
        ) : (
          <>
            <span className="icon">
              <QuincyIcon />
            </span>
            <span className="name">{t("pages.ReleasesSchedule.onlyQuincy.quincyIA")}</span>
          </>
        )}
      </div>
      <div className="body">
        {currentAction === CurrentAction.Generating && (
          <span className="quincy-loading">
            <Spin
              className="spin-loading"
              indicator={<LoadingOutlined spin />}
            />
            {t("pages.ReleasesSchedule.onlyQuincy.gerandoSeuCronograma")}
          </span>
        )}
        <Form.Item
          name={fieldName}
          rules={required ? [{ required: true }] : undefined}
          hidden={currentAction === CurrentAction.Generating}
        >
          <TextArea
            className={`textarea-quincy ${currentAction === CurrentAction.None ? "readonly" : ""
              }`}
            rows={12}
            readOnly={currentAction === CurrentAction.None}
          />
        </Form.Item>
        {currentAction !== CurrentAction.Generating && (
          <div className="div-count">
            {currentAction === CurrentAction.Editing && (
              <div>
                <Button className="botao-branco" onClick={onConfirmEdit}>
                  {t("pages.ReleasesSchedule.onlyQuincy.confirmar")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {currentAction === CurrentAction.None && (
        <div className="footer">
          <div className="buttons">
            <button className="round" onClick={onEdit} type="button">
              <PencilIcon />
            </button>
            <button
              className="btn-generate-pitching"
              type="button"
              onClick={regenerateQuincy}
            >
              <QuincyIcon />
              <span>{t("pages.ReleasesSchedule.onlyQuincy.gerarNovamente")}</span>
            </button>
          </div>
          <div className="div-prompt">
            <Input
              className="prompt-chat"
              placeholder={t("pages.ReleasesSchedule.onlyQuincy.quincyPlaceholder")}
              value={prompt}
              suffix={
                <button
                  className="btn-prompt-icon"
                  type="button"
                  disabled={!prompt}
                  onClick={regenerateQuincy}
                >
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="input-prompt-icon"
                    color="#0D0D0D"
                  />
                </button>
              }
              onChange={(ev) => setPrompt(ev.target.value)}
              onKeyDown={onKeyDownPrompt}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReleaseScheduleQuincy;
